import { Component, HostListener, OnInit } from '@angular/core';
import { MenuItem } from 'primeng/api';
import { LayoutService } from './service/app.layout.service';
import { WebStorage } from '../demo/components/auth/storage/web.storage';
import { Router, RouterLink } from '@angular/router';
import { Common } from '../demo/utils/common';
import { ChatNavigationService } from '../demo/service/chat-navigation.service';

@Component({
    selector: 'app-menu',
    templateUrl: './app.menu.component.html',
})
export class AppMenuComponent implements OnInit {
    model: MenuItem[] = [];
    isMobile!: boolean;
    hasDocumentAuthorityLastPhysician = this.storage.hasAnyAuthority([
        'Staff Profile (Full Access)',
        'Staff Profile (Read)',
    ]);
    activeStudent: any;

    isStaffUser(): boolean {
        const user = this.storage.getUserData();
        if (!user) return false;

        const refTable = (user?.refTable || '').toString().trim().toLowerCase();
        const authorities = user?.authorities || [];
        const isStaff = refTable === 'staff';
        const isAdmin = authorities.includes('Role Admin');

        return isStaff && !isAdmin;
    }

    isGuardian(): boolean {
        const user = this.storage.getUserData();
        if (!user) return false;
        return (user?.refTable || '').toString().trim().toLowerCase() === 'guardian';
    }

    getMyAttendanceRoute(): string[] {
        const user = this.storage.getUserData();
        if (user?.refTableId) {
            return ['/staff/viewAttendance/staffAttendanceProfile', user.refTableId.toString()];
        }
        return ['/'];
    }

    /**
     * Return the routerLink for the Student Attendance menu item.
     * - If the logged-in user is a Guardian, redirect directly to the
     *   active student's profile view attendance page: /student-Sign-In/profileViewAttendance/:id
     * - Otherwise return the normal student sign-in dailyAttendance path.
     */
    getStudentAttendanceLink(): any[] {
        const user = this.storage.getUserData();
        if (user && user.refTable && user.refTable.toString().toLowerCase() === 'guardian') {
            // Try to get active student id from localStorage (activeStudent) or from stored guardian data
            const activeStudentRaw = localStorage.getItem('activeStudent');
            let studentId = null;
            try {
                const parsed = activeStudentRaw ? JSON.parse(activeStudentRaw) : null;
                studentId = parsed?.id || null;
            } catch (e) {
                studentId = null;
            }

            if (studentId) {
                return ['/student-Sign-In/profileViewAttendance', studentId.toString()];
            }

            // Fallback to the general customAttendance if we don't know the student id
            return ['/student-Sign-In/profileViewAttendance'];
        }

        // Default for non-guardian users
        return ['/student-Sign-In/dailyAttendance'];
    }

    goToStudentMessaging(): void {
        this.chatNavService.setTabNavigation('student');
        this.router.navigate(['/communication'], { state: { initialTab: 'student' } });
    }

    goToStaffMessaging(): void {
        this.chatNavService.setTabNavigation('staff');
        this.router.navigate(['/communication'], { state: { initialTab: 'staff' } });
    }

    // Desktop menu model (original)
    desktopModel: MenuItem[] = [
        {
            label: 'Dashboard',
            icon: 'wow wow-dashboard',
            routerLink: ['/'],
        },
        {
            label: 'Classrooms',
            icon: 'wow wow-classroom',
            routerLink: ['/myschool/room'],
            visible: this.storage.hasAnyAuthority([
                'Room Profile (Read)',
                'Room Profile (Full Access)',
            ]),
        },
        {
            label: 'Students',
            icon: 'wow wow-student',
            routerLink: ['/myschool'],
            items: [
                {
                    label: 'Overview',
                    icon: 'pi pi-user',
                    routerLink: ['/myschool/student'],
                    visible: this.storage.hasAnyAuthority([
                        'Student Profile (Full Access)',
                        'Student Profile (Read)',
                        'Student (Full Access)',
                        'Student (Read)',
                    ]),
                },
                {
                    label: 'Tags',
                    icon: 'pi pi-book',
                    routerLink: ['/myschool/student/tags'],
                    visible: this.storage.hasAnyAuthority([
                        'Tag (Full Access)',
                    ]),
                },
            ],
        },
        {
            label: 'Staff',
            icon: 'wow wow-staff',
            routerLink: ['/staff/overview'],
            // visible: this.storage.hasAnyAuthority([
            //     'Staff Profile (Full Access)',
            //     'Staff Profile (Read)',
            //     'Staff (Full Access)',
            //     'Staff (Read)',
            // ]),
            visible: Common.isAdminUserLogin(this.storage)
        },
        {
            label: 'Attendance',
            icon: 'wow wow-attendance',
            visible: this.storage.hasAnyAuthority([
                'Attendance Daily (Full Access)',
                'Attendance Daily (Read)',
                'Attendance Monthly (Read)',
                'Attendance Absent (Full Access)',
                'Attendance Absent (Read)',
            ]),
            // For guardians make Attendance a direct link to the active student's profile view.
            routerLink: this.isGuardian() ? this.getStudentAttendanceLink() : undefined,
            // For non-guardian roles show the regular sub-items.
            items: this.isGuardian() ? undefined : [
                {
                    label: 'Student Attendance',
                    icon: 'wow wow-student-attendance',
                    routerLink: [
                        '/student-Sign-In/dailyAttendance',
                    ],
                    visible: this.storage.hasAnyAuthority([
                        'Attendance Daily (Full Access)',
                        'Attendance Daily (Read)',
                    ]),
                },
                {
                    label: 'Staff Attendance',
                    icon: 'wow wow-staffAttendance',
                    visible: this.storage.hasAnyAuthority([
                        'Staff Attendance (Full Access)',
                    ]),
                    routerLink: [
                        '/staff/viewAttendance/daily-view',
                    ],
                },
                {
                    label: 'My Attendance',
                    icon: 'wow wow-staffAttendance',
                    visible: this.isStaffUser(),
                    routerLink: ['/staff/viewAttendance/staffAttendanceProfile/', (this.storage.getUserData()?.refTableId || '')]
                },
            ],
        },
        {
            label: 'Daily Activity',
            icon: 'wow wow-activity',
            routerLink: ['parentConnection/activities/all'],
            visible: this.storage.hasAnyAuthority([
                'Parent Connection Newsletter (Full Access)',
                'Parent Connection Newsletter (Read)',
                'Parent Connection Daily Activity (Read)',
                'Parent Connection Daily Activity (Full Access)',
                'Parent Connection Daily Activity Feed (Read)',
                'Parent Connection Daily Activity Feed (Full Access)',
            ]),
        },
        {
            label: 'Communication',
            icon: 'wow wow-communication',
            routerLink: ['/parentConnection'],
            visible: this.storage.hasAnyAuthority([
                'Parent Connection Newsletter (Full Access)',
                'Parent Connection Newsletter (Read)',
                'Parent Connection Message (Full Access)',
                'Prent Connection Message (Read)',
                'Parent Connection Daily Activity (Read)',
                'Parent Connection Daily Activity (Full Access)',
                'Parent Connection Daily Activity (Read)',
                'Parent Connection Daily Activity Feed (Full Access)',
            ]),
            items: [
                {
                    label: 'Newsletter',
                    icon: 'pi pi-id-card',
                    routerLink: ['parentConnection/newsletter/sentLetter'],
                    visible: this.storage.hasAnyAuthority([
                        'Parent Connection Newsletter (Full Access)',
                        'Parent Connection Newsletter (Read)',
                    ]),
                },
                {
                    label: 'Parent Messaging',
                    icon: 'wow wow-communication',
                    command: () => this.goToStudentMessaging(),
                    visible: this.storage.hasAnyAuthority([
                        'Parent Connection Message (Full Access)',
                        'Prent Connection Message (Read)',
                    ]),
                },
                {
                    label: 'Staff Messaging',
                    icon: 'wow wow-communication',
                    command: () => this.goToStaffMessaging(),
                    visible: this.storage.hasAnyAuthority([
                        'Staff Message (Full Access)',
                        'Staff Message (Read)',
                    ]),
                },
            ],
        },
        {
            label: 'Scheduling',
            icon: 'wow wow-schedule',
            visible: this.storage.hasAnyAuthority([
                'Staff Scheduling (Full Access)',
                'Staff Scheduling (Read)'
            ]),
            items: [
                {
                    label: 'Classroom Schedule',
                    icon: 'wow wow-class_schedule_time',
                    routerLink: ['/scheduling/classroom-view'],
                    visible: this.storage.hasAnyAuthority([
                        'Staff Scheduling (Full Access)',
                    ]),
                },
                {
                    label: 'Staff Schedule',
                    icon: 'wow wow-scheule_time',
                    routerLink: ['/scheduling/staff-view'],
                    visible: this.storage.hasAnyAuthority([
                        'Staff Scheduling (Full Access)',
                        'Staff Scheduling (Read)'
                    ]),
                },
               
            ],
        },
        {
            label: 'Billing',
            icon: 'wow wow-billing',
            routerLink: ['/billing'],
            visible: this.storage.hasAnyAuthority([
                'Billing Accounting (Full Access)',
                'Billing Accounting (Read)',
                'Billing Recurring Active Plans (Full Access)',
                'Billing Recurring Active Plans (Read)',
                'Billing Recurring Plan Templates (Full Access)',
                'Billing Recurring Plan Templates (Read)',
                'All Transaction List (Full Access)',
                'All Transaction List (Read)',
            ]),
            items: [
                {
                    label: 'Accounting',
                    icon: 'pi pi-calculator',
                    routerLink: ['/billing/accounting/family'],
                    visible: this.storage.hasAnyAuthority([
                        'Billing Accounting (Full Access)',
                    ]),
                },
                {
                    label: 'Recurring Plans',
                    icon: 'pi pi-history',
                    routerLink: ['/billing/plans'],
                    visible: this.storage.hasAnyAuthority([
                        'Billing Recurring Plan Templates (Full Access)',
                        'Billing Recurring Plan Templates (Read)',
                    ]),
                },
                {
                    label: 'Transactions',
                    icon: 'pi pi-dollar',
                    routerLink: ['/billing/alltransaction'],
                    visible: this.storage.hasAnyAuthority([
                        'All Transaction List (Full Access)',
                        'All Transaction List (Read)',
                    ]),
                },
            ],
        },
        {
            label: 'Report',
            icon: 'wow wow-report',
            routerLink: ['/reports/report'],
            visible: this.storage.hasAnyAuthority([
                'Reports (Full Access)',
                'Reports (Read)',
            ]),
        },
        {
            label: 'Availability',
            icon: 'wow wow-availability',
            visible: this.storage.hasAnyAuthority([
                'Room Move (Full Access)',
                'Enrollment (Full Access)',
                'Availability (Read)',
            ]),
            items: [
                {
                    label: 'Movement Rules',
                    routerLink: './future-enrollment/room-movement',
                    visible: this.storage.hasAnyAuthority([
                        'Room Move (Full Access)',
                        'Room Move (Read)',
                    ]),
                },
                {
                    label: 'Individual Movements',
                    routerLink: ['./future-enrollment/overview'],
                    visible: this.storage.hasAnyAuthority([
                        'Enrollment (Full Access)',
                    ]),
                },
                {
                    label: 'Future Availability',
                    routerLink: ['./future-enrollment/future-availability'],
                    visible: this.storage.hasAnyAuthority([
                        'Availability (Read)',
                    ]),
                },
            ],
        },
        {
            label: 'Settings',
            icon: 'wow wow-setting',
            routerLink: ['/settings'],
            visible: this.storage.hasAnyAuthority([
                'School Settings General (Edit)',
                'School Settings Immunization (Full Access)',
                'School Settings Immunization (Read)',
                'School Settings Custom Fields (Full Access)',
                'School Settings Custom Fields (Read)',
            ]),
            items: [
                {
                    label: 'School Settings',
                    icon: 'pi pi-calculator',
                    routerLink: ['/settings/school'],
                    visible: this.storage.hasAnyAuthority([
                        'School Settings General (Edit)',
                        'School Settings Immunization (Full Access)',
                        'School Settings Immunization (Read)',
                        'School Settings Custom Fields (Full Access)',
                        'School Settings Custom Fields (Read)',
                    ]),
                },
                {
                    label: 'Users',
                    icon: 'pi pi-user',
                    routerLink: ['/gen-settings/users'],
                    visible: this.storage.hasAnyAuthority([
                        'Staff (Full Access)',
                    ]),
                },
            ],
        },
    ];

    // Mobile menu model (all items wrapped in items array)
    mobileModel: MenuItem[] = [
        {
            label: 'Dashboard',
            icon: 'wow wow-dashboard',
            items: [
                {
                    label: 'Dashboard',
                    icon: 'wow wow-dashboard',
                    routerLink: ['/'],
                },
            ],
        },
        {
            label: 'Classrooms',
            icon: 'wow wow-classroom',
            visible: this.storage.hasAnyAuthority([
                'Room Profile (Read)',
                'Room Profile (Full Access)',
            ]),
            items: [
                {
                    label: 'Classrooms',
                    icon: 'wow wow-classroom',
                    routerLink: ['/myschool/room'],
                    visible: this.storage.hasAnyAuthority([
                        'Room Profile (Read)',
                        'Room Profile (Full Access)',
                    ]),
                },
            ],
        },
        {
            label: 'Students',
            icon: 'wow wow-student',
            items: [
                {
                    label: 'Overview',
                    icon: 'pi pi-user',
                    routerLink: ['/myschool/student'],
                    visible: this.storage.hasAnyAuthority([
                        'Student Profile (Full Access)',
                        'Student Profile (Read)',
                        'Student (Full Access)',
                        'Student (Read)',
                    ]),
                },
                {
                    label: 'Tags',
                    icon: 'pi pi-book',
                    routerLink: ['/myschool/student/tags'],
                    visible: this.storage.hasAnyAuthority([
                        'Tag (Full Access)',
                    ]),
                },
            ],
        },
        {
            label: 'Staff',
            icon: 'wow wow-staff',
            items: [
                {
                    label: 'Staff',
                    icon: 'wow wow-staff',
                    routerLink: ['/staff/overview'],
                    visible: Common.isAdminUserLogin(this.storage)

                },
            ],
        },
        {
            label: 'Attendance',
            icon: 'wow wow-attendance',
            visible: this.storage.hasAnyAuthority([
                'Attendance Daily (Full Access)',
                'Attendance Daily (Read)',
                'Attendance Monthly (Read)',
                'Attendance Absent (Full Access)',
                'Attendance Absent (Read)',
            ]),
            items: [
                {
                    label: 'Student Attendance',
                    icon: 'wow wow-student-attendance',
                    routerLink: [
                        '/student-Sign-In/dailyAttendance',
                    ],
                    visible: this.storage.hasAnyAuthority([
                        'Attendance Daily (Full Access)',
                        'Attendance Daily (Read)',
                    ]),
                },
                {
                    label: 'Staff Attendance',
                    icon: 'wow wow-staffAttendance',
                    visible: this.storage.hasAnyAuthority([
                        'Staff Attendance (Full Access)',
                    ]),
                    routerLink: [
                        '/staff/viewAttendance/daily-view',
                    ],
                },
                {
                    label: 'My Attendance',
                    icon: 'wow wow-staffAttendance',
                    visible: this.isStaffUser(),
                    routerLink: ['/staff/viewAttendance/staffAttendanceProfile/', (this.storage.getUserData()?.refTableId || '')]
                },
            ],
        },
        {
            label: 'Daily Activity',
            icon: 'wow wow-activity',
            items: [
                {
                    label: 'Daily Activity',
                    icon: 'wow wow-activity',
                    routerLink: ['parentConnection/activities/all'],
                    visible: this.storage.hasAnyAuthority([
                        'Parent Connection Newsletter (Full Access)',
                        'Parent Connection Newsletter (Read)',
                        'Parent Connection Daily Activity (Read)',
                        'Parent Connection Daily Activity (Full Access)',
                        'Parent Connection Daily Activity Feed (Read)',
                        'Parent Connection Daily Activity Feed (Full Access)',
                    ]),
                },
            ],
        },
        {
            label: 'Communication',
            icon: 'wow wow-communication',
            items: [
                {
                    label: 'Newsletter',
                    icon: 'pi pi-id-card',
                    routerLink: ['parentConnection/newsletter/sentLetter'],
                    visible: this.storage.hasAnyAuthority([
                        'Parent Connection Newsletter (Full Access)',
                        'Parent Connection Newsletter (Read)',
                    ]),
                },
                {
                    label: 'Parent Messaging',
                    icon: 'wow wow-communication',
                    command: () => this.goToStudentMessaging(),
                    visible: this.storage.hasAnyAuthority([
                        'Parent Connection Message (Full Access)',
                        'Prent Connection Message (Read)',
                    ]),
                },
                {
                    label: 'Staff Messaging',
                    icon: 'wow wow-communication',
                    command: () => this.goToStaffMessaging(),
                    visible: this.storage.hasAnyAuthority([
                        'Staff Message (Full Access)',
                        'Staff Message (Read)',
                    ]),
                },
            ],
        },
        {
            label: 'Scheduling',
            icon: 'wow wow-schedule',
            visible: this.storage.hasAnyAuthority([
                'Staff Scheduling (Full Access)',
                'Staff Scheduling (Read)'
            ]),
            items: [
                {
                    label: 'Classroom Schedule',
                    icon: 'wow wow-class_schedule_time',
                    routerLink: ['/scheduling/classroom-view'],
                    visible: this.storage.hasAnyAuthority([
                        'Staff Scheduling (Full Access)',
                    ]),
                },
                {
                    label: 'Staff Schedule',
                    icon: 'wow wow-scheule_time',
                    routerLink: ['/scheduling/staff-view'],
                    visible: this.storage.hasAnyAuthority([
                        'Staff Scheduling (Full Access)',
                        'Staff Scheduling (Read)'
                    ]),
                },
            ],
        },
        {
            label: 'Billing',
            icon: 'wow wow-billing',
            visible: this.storage.hasAnyAuthority([
                'Billing Accounting (Full Access)',
                'Billing Accounting (Read)',
                'Billing Recurring Active Plans (Full Access)',
                'Billing Recurring Active Plans (Read)',
                'Billing Recurring Plan Templates (Full Access)',
                'Billing Recurring Plan Templates (Read)',
                'All Transaction List (Full Access)',
                'All Transaction List (Read)',
            ]),
            items: [
                {
                    label: 'Accounting',
                    icon: 'pi pi-calculator',
                    routerLink: ['/billing/accounting/family'],
                    visible: this.storage.hasAnyAuthority([
                        'Billing Accounting (Full Access)',
                    ]),
                },
                {
                    label: 'Recurring Plans',
                    icon: 'pi pi-history',
                    routerLink: ['/billing/plans'],
                    visible: this.storage.hasAnyAuthority([
                        'Billing Recurring Plan Templates (Full Access)',
                        'Billing Recurring Plan Templates (Read)',
                    ]),
                },
                {
                    label: 'Transactions',
                    icon: 'pi pi-dollar',
                    routerLink: ['/billing/alltransaction'],
                    visible: this.storage.hasAnyAuthority([
                        'All Transaction List (Full Access)',
                        'All Transaction List (Read)',
                    ]),
                },
            ],
        },
        {
            label: 'Report',
            icon: 'wow wow-report',
            visible: this.storage.hasAnyAuthority([
                'Reports (Full Access)',
                'Reports (Read)',
            ]),
            items: [
                {
                    label: 'Report',
                    icon: 'wow wow-report',
                    routerLink: ['/reports/report'],
                    visible: this.storage.hasAnyAuthority([
                        'Reports (Full Access)',
                        'Reports (Read)',
                    ]),
                },
            ],
        },
        {
            label: 'Availability',
            icon: 'wow wow-availability',
            visible: this.storage.hasAnyAuthority([
                'Room Move (Full Access)',
                'Enrollment (Full Access)',
                'Availability (Read)',
            ]),
            items: [
                {
                    label: 'Movement Rules',
                    routerLink: './future-enrollment/room-movement',
                    visible: this.storage.hasAnyAuthority([
                        'Room Move (Full Access)',
                        'Room Move (Read)',
                    ]),
                },
                {
                    label: 'Individual Movements',
                    routerLink: ['./future-enrollment/overview'],
                    visible: this.storage.hasAnyAuthority([
                        'Enrollment (Full Access)',
                    ]),
                },
                {
                    label: 'Future Availability',
                    routerLink: ['./future-enrollment/future-availability'],
                    visible: this.storage.hasAnyAuthority([
                        'Availability (Read)',
                    ]),
                },
            ],
        },
        {
            label: 'Settings',
            icon: 'wow wow-setting',
            visible: this.storage.hasAnyAuthority([
                'School Settings General (Edit)',
                'School Settings Immunization (Full Access)',
                'School Settings Immunization (Read)',
                'School Settings Custom Fields (Full Access)',
                'School Settings Custom Fields (Read)',
            ]),
            items: [
                {
                    label: 'School Settings',
                    icon: 'pi pi-calculator',
                    routerLink: ['/settings/school'],
                    visible: this.storage.hasAnyAuthority([
                        'School Settings General (Edit)',
                        'School Settings Immunization (Full Access)',
                        'School Settings Immunization (Read)',
                        'School Settings Custom Fields (Full Access)',
                        'School Settings Custom Fields (Read)',
                    ]),
                },
                {
                    label: 'Users',
                    icon: 'pi pi-user',
                    routerLink: ['/gen-settings/users'],
                    visible: this.storage.hasAnyAuthority([
                        'Staff (Full Access)',
                    ]),
                },
            ],
        },
    ];

    constructor(
        public layoutService: LayoutService,
        private storage: WebStorage,
        private router: Router,
        private chatNavService: ChatNavigationService
    ){}

    @HostListener('window:resize', ['$event'])
    onResize(event: any) {
        this.isMobile = event.target.innerWidth < 1000;
        this.updateMenu();
    }

    ngOnInit() {
        this.isMobile = window.innerWidth < 1000;
        this.updateMenu();
      
        let attempts = 0;
        const maxAttempts = 10; 
        const poller = setInterval(() => {
          const raw = localStorage.getItem('activeStudent');
          if (raw || attempts >= maxAttempts) {
            clearInterval(poller);
            try {
              this.activeStudent = raw ? JSON.parse(raw) : null;
              this.updateMenu();
            } catch (e) {
              console.error('Failed to parse activeStudent:', e);
            }
          }
          attempts++;
        }, 300);

            const user = this.storage.getUserData();

      }
      

    // updateMenu() {
    //     // Deep clone the base model to avoid mutating the original
    //     this.model = this.isMobile ? JSON.parse(JSON.stringify(this.mobileModel)) : JSON.parse(JSON.stringify(this.desktopModel));

    //     // Add menu items that depend on activeStudent
    //     if (this.activeStudent?.id) {
    //         // Example: Add a Family Transaction item under Billing
    //         const billingMenu = this.model.find(item => item.label === 'Billing');
    //         if (billingMenu && billingMenu.items) {
    //             billingMenu.items.push({
    //                 label: 'Family Transaction',
    //                 icon: 'pi pi-dollar',
    //                 routerLink: ['/billing/accounting/family-transaction/' + this.activeStudent.id]
    //             });
    //         }
    //     }
    // }

       updateMenu() {
        this.model = this.isMobile ? JSON.parse(JSON.stringify(this.mobileModel)) : JSON.parse(JSON.stringify(this.desktopModel));

        // Wire commands for communication items after cloning (JSON.stringify strips functions)
        const communicationMenu = this.model.find(item => item.label === 'Communication');
        if (communicationMenu && communicationMenu.items) {
            const parentMessaging = communicationMenu.items.find(item => item.label === 'Parent Messaging');
            if (parentMessaging) {
                parentMessaging.command = () => this.goToStudentMessaging();
            }
            const staffMessaging = communicationMenu.items.find(item => item.label === 'Staff Messaging');
            if (staffMessaging) {
                staffMessaging.command = () => this.goToStaffMessaging();
            }
        }

           const user = this.storage.getUserData();
            const refTable = (user?.refTable || '').toString().trim().toLowerCase();
            const authorities = user?.authorities || [];
            const isAdmin = authorities.includes('Role Admin');

            // Only for staff (not admin): make Students menu route directly to overview
            if (refTable === 'staff' && !isAdmin) {
                const studentsMenu = this.model.find(item => item.label === 'Students');
                if (studentsMenu) {
                    if (this.isMobile) {
                        // For mobile: keep wrapper, but change the inner item
                        studentsMenu.items = [
                            {
                                label: 'Overview',
                                icon: 'pi pi-user',
                                routerLink: ['/myschool/student'],
                                visible: this.storage.hasAnyAuthority([
                                    'Student Profile (Full Access)',
                                    'Student Profile (Read)',
                                    'Student (Full Access)',
                                    'Student (Read)',
                                ]),
                            }
                        ];
                    } else {
                        // For desktop: remove submenu and route directly
                        studentsMenu.routerLink = ['/myschool/student'];
                        delete studentsMenu.items;
                        studentsMenu.visible = this.storage.hasAnyAuthority([
                            'Student Profile (Full Access)',
                            'Student Profile (Read)',
                            'Student (Full Access)',
                            'Student (Read)',
                        ]);
                    }
                }

                const schedulingMenu = this.model.find(item => item.label === 'Scheduling');
                if (schedulingMenu) {
                    if (this.isMobile) {
                        // For mobile: show submenu with only Staff Schedule
                        schedulingMenu.items = [
                            {
                                label: 'Staff Schedule',
                                icon: 'wow wow-scheule_time',
                                routerLink: ['/scheduling/staff-view'],
                                visible: this.storage.hasAnyAuthority([
                                    'Staff Scheduling (Full Access)',
                                    'Staff Scheduling (Read)'
                                ]),
                            }
                        ];
                    } else {
                        // For desktop: direct link, no submenu
                        schedulingMenu.routerLink = ['/scheduling/staff-view'];
                        delete schedulingMenu.items;
                        schedulingMenu.visible = this.storage.hasAnyAuthority([
                            'Staff Scheduling (Full Access)',
                            'Staff Scheduling (Read)'
                        ]);
                    }
                }
            }

        if (this.activeStudent?.id) {
            // Check if user is a parent/guardian
            const user = this.storage.getUserData();
            const refTable = (user?.refTable || '').toString().trim().toLowerCase();
            const isParentRole = refTable === 'parent' || refTable === 'guardian';

            if (isParentRole) {
                // For parents/guardians: make the top-level Billing item navigate directly
                // to the family-transaction page for the active student and remove the submenu.
                const billingMenu = this.model.find(item => item.label === 'Billing');
                if (billingMenu) {
                    billingMenu.routerLink = ['/billing/accounting/familylayout/family-transaction'];
                    billingMenu.queryParams = { id: this.activeStudent.id };
                    billingMenu.items = undefined;
                }
            }
            // Also make Students top-level navigate directly to the active student's profile for guardians
            if (isParentRole) {
                const studentsMenu = this.model.find(item => item.label === 'Students');
                if (studentsMenu) {
                    studentsMenu.routerLink = ['/myschool/student/profile', this.activeStudent.id, 'personal'];
                    studentsMenu.items = undefined;
                    // Rename label for guardian users
                    studentsMenu.label = 'My Child';
                }
            }
        }
    }

}



